import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LayoutDashboard,
    Users,
    ListChecks,
    Inbox,
    MessageCircle,
    Sparkles,
    LogOut,
    Search,
    Target,
} from "lucide-react";
import { ElateWordmark, ElateMark } from "./elate/ElateLogo";
import { useAuth } from "../lib/auth";
import { CommandBar } from "./CommandBar";

const NAV = [
    { to: "/app", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/app/goals", icon: Target, label: "Goals", testid: "nav-goals" },
    { to: "/app/clients", icon: Users, label: "Clients", testid: "nav-clients" },
    { to: "/app/tasks", icon: ListChecks, label: "Tasks", testid: "nav-tasks" },
    { to: "/app/leads", icon: Inbox, label: "Leads", testid: "nav-leads" },
    { to: "/app/conversations", icon: MessageCircle, label: "WhatsApp", testid: "nav-conversations" },
];

export function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [cmdOpen, setCmdOpen] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen((o) => !o);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const title =
        NAV.find((n) =>
            n.to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(n.to)
        )?.label || "Elate OS";

    return (
        <div className="min-h-screen bg-[#050505] text-white relative">
            {/* Ambient orbs */}
            <div className="orb" style={{ top: -120, left: -120, width: 380, height: 380, background: "rgba(0,102,255,0.18)" }} />
            <div className="orb" style={{ bottom: -160, right: -160, width: 520, height: 520, background: "rgba(255,85,0,0.10)" }} />

            <div className="relative z-10 flex">
                {/* Slim left rail */}
                <aside className="hidden md:flex w-20 lg:w-60 shrink-0 border-r border-white/[0.06] min-h-screen flex-col py-6 px-3 lg:px-4">
                    <div className="px-1 lg:px-2 mb-10">
                        <div className="hidden lg:block">
                            <ElateWordmark />
                        </div>
                        <div className="lg:hidden flex justify-center">
                            <ElateMark />
                        </div>
                    </div>

                    <nav className="space-y-1 flex-1">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/app"}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all ${
                                        isActive
                                            ? "bg-white/[0.06] text-white"
                                            : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                                    }`
                                }
                            >
                                <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                                <span className="hidden lg:inline">{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    <button
                        data-testid="nav-command-bar"
                        onClick={() => setCmdOpen(true)}
                        className="hidden lg:flex w-full items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-xs text-zinc-400 hover:text-white hover:bg-white/[0.04] transition mb-3"
                    >
                        <Sparkles className="h-3.5 w-3.5 text-[#0066FF]" />
                        <span>Ask Ana</span>
                        <kbd className="ml-auto font-mono text-[10px] text-zinc-500">⌘K</kbd>
                    </button>

                    <button
                        data-testid="nav-logout"
                        onClick={async () => {
                            await logout();
                            navigate("/login");
                        }}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-zinc-500 hover:text-white hover:bg-white/[0.04] transition"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={1.6} />
                        <span className="hidden lg:inline">Sign out</span>
                    </button>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0">
                    {/* Top bar */}
                    <header className="sticky top-0 z-30 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/[0.06]">
                        <div className="flex items-center justify-between px-5 lg:px-10 py-4">
                            <div className="flex items-center gap-3">
                                <div className="md:hidden">
                                    <ElateMark />
                                </div>
                                <h1 className="font-display text-lg sm:text-xl font-medium tracking-tight">
                                    {title}
                                </h1>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    data-testid="topbar-command-bar"
                                    onClick={() => setCmdOpen(true)}
                                    className="flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-white/[0.05] transition"
                                >
                                    <Search className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">Ask Ana anything…</span>
                                    <kbd className="font-mono text-[10px] text-zinc-500 hidden sm:inline">⌘K</kbd>
                                </button>
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#0066FF] to-[#FF5500] grid place-items-center text-[11px] font-semibold uppercase">
                                        {(user?.name || user?.email || "?").slice(0, 1)}
                                    </div>
                                    <div className="hidden sm:block text-xs">
                                        <div className="text-white leading-tight" data-testid="user-name">{user?.name || "User"}</div>
                                        <div className="text-zinc-500 leading-tight">{user?.role || "user"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="px-5 lg:px-10 py-8"
                        >
                            {children}
                        </motion.div>
                    </AnimatePresence>
                </main>
            </div>

            <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} />
        </div>
    );
}
